import { useCallback } from 'react';

import { useAuthorizedUser } from 'Services/User/useAuthorizedUser';
import { useSignUpModal } from 'Shared/Auth/AuthModal/useSignUpModal';
import { useSignInModal } from 'Shared/Auth/AuthModal/useSignInModal';

import type { EventT, CallbackT, OptionsT } from './types';

export const useWithAuthorizationCheck = (
  callback: CallbackT,
  {
    triggeredBy,
    authType = 'signin',
    nextFocusTargetElement,
    shouldRemoveAllOnHistoryBack,
  }: OptionsT = {},
) => {
  const { isAuthorized } = useAuthorizedUser();
  const { openSignInModal } = useSignInModal();
  const { openSignUpModal } = useSignUpModal();

  const handleUnauthorized = useCallback(() => {
    switch (authType) {
      case 'signin': {
        openSignInModal({
          triggeredBy,
          nextFocusTargetElement,
          shouldRemoveAllOnHistoryBack,
        });
        break;
      }
      case 'signup': {
        openSignUpModal({
          triggeredBy,
          nextFocusTargetElement,
          shouldRemoveAllOnHistoryBack,
        });
        break;
      }
      default: {
        throw new Error('Unknown authType');
      }
    }
  }, [
    authType,
    openSignInModal,
    triggeredBy,
    nextFocusTargetElement,
    shouldRemoveAllOnHistoryBack,
    openSignUpModal,
  ]);

  return useCallback(
    (evt: EventT) => {
      evt.preventDefault();
      if (!isAuthorized) {
        handleUnauthorized();

        return;
      }

      // eslint-disable-next-line consistent-return
      return callback(evt);
    },
    [handleUnauthorized, isAuthorized, callback],
  );
};
